/**
 * PrototypePill — inline chip rendered under an assistant message when
 * the SSE stream produced a `prototype_ready` event. Mirrors
 * {@link ArtifactPill} in style so the two indicators sit side-by-side
 * without visual dissonance.
 *
 * Clicking the pill re-opens the prototyper panel — for the initial turn
 * the panel is already auto-opened by the headers facade; the pill's
 * value is that it re-opens the panel on later visits (history load) and
 * after the user manually closed it.
 */

import React from 'react';
import { ExternalLink, Rocket } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface PrototypePillProps {
  /** Called when the user clicks the pill. Host owns the panel-open flow. */
  onOpen?: () => void;
  /** RTL from the parent message. */
  isRtl?: boolean;
  /** Extra classes for positioning inside MessageItem. */
  className?: string;
  /** Optional label suffix (e.g. version index). */
  label?: string;
  /** Highlight when this version is loaded in the WebContainer. */
  isActive?: boolean;
  /** Show a loading state while restoring this version. */
  isLoading?: boolean;
}

export const PrototypePill: React.FC<PrototypePillProps> = ({
  onOpen,
  isRtl,
  className,
  label,
  isActive = false,
  isLoading = false,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col rounded-xl border bg-white w-full',
        isActive ? 'border-primary-400 ring-1 ring-primary-200' : 'border-gray-200',
        'dark:border-gray-700 dark:bg-gray-900',
        className
      )}
      data-testid="prototype-pill"
    >
      <button
        type="button"
        onClick={() => onOpen?.()}
        disabled={isLoading}
        className={cn(
          'flex items-center gap-2 text-left text-sm text-gray-700 dark:text-gray-200 w-full px-6 py-6',
          onOpen && !isLoading && 'hover:text-primary-600 dark:hover:text-primary-400',
          isLoading && 'opacity-70 cursor-wait',
        )}
        data-testid="prototype-pill-open"
      >
        <Rocket size={24} className="shrink-0 text-primary-500" />
        <span className="text-md">
          <span className="text-gray-400 dark:text-gray-500">
            {isActive ? 'Active prototype — ' : 'Prototype — '}
          </span>
          {isLoading ? 'Restoring…' : (label ?? 'View prototype')}
        </span>
      </button>
    </div>
  );
};

export default PrototypePill;
